import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Redirect } from 'react-router-dom';
import {
  ARRAY_VALUE_TYPE,
  STRING_VALUE_TYPE,
  BOOLEAN_VALUE_TYPE,
  NUMBER_VALUE_TYPE,
  ENUM_VALUE_TYPE,
} from './Types';

const sameArrays = (ar1, ar2) => {
  if (!ar1 || !ar1.length) {
    return !ar2 || (ar2.length === 0);
  }
  if (!ar2 || (ar2.length !== ar1.length)) {
    return false;
  }
  return !ar1.find(e1 => ar2.indexOf(e1) < 0);
};

const TYPES = {
  [ARRAY_VALUE_TYPE]: {
    toQuery(f) {
      // When returning an array [value1, value2] (f is an array),
      // a query will be generated with the form key=[value1]&key=[value2]
      return (this && this.useMultipleKeys) ? f : f.join(',');
    },
    toFilter: (q) => {
      // If array value is specified with the format ?key=value1&key=value2 in the query
      // q is provided as the array [value1, value2].
      // Otherwise, the value is provided as a comma separated value list in a string: "value1,value2"
      return Array.isArray(q) ? q : q.split(',');
    },
    sameValues: sameArrays,
    createObjectFilter: (ar, props, key) => (o) => {
      return ar.indexOf(o[key]) >= 0;
    },
  },
  number: {
    toQuery: f => `${f}`,
    toFilter: (q) => {
      return parseInt(q, 10);
    },
    sameValues: (o1, o2) => o1 === o2,
  },
  [STRING_VALUE_TYPE]: {
    toQuery: f => f,
    toFilter: q => q,
    sameValues: (o1, o2) => o1 === o2,
  },
  [BOOLEAN_VALUE_TYPE]: {
    toQuery: b => (b ? 'true' : 'false'),
    toFilter: s => (s === 'true'),
    sameValues: (o1, o2) => o1 === o2,
  },
  [NUMBER_VALUE_TYPE]: {
    toQuery: n => (n ? `${n}` : '0'),
    toFilter: s => (s === '' ? 0 : Number(s)),
    sameValues: (o1, o2) => o1 === o2,
  },
};

const procesEditor = (pe) => {
  const prop = { ...pe, type: pe.type === ENUM_VALUE_TYPE ? STRING_VALUE_TYPE : pe.type };
  if (!prop.keys) {
    // Filter for this property editor is associated with only one filter query parameter.
    // For example, a filter on the object id will be associated with a filterKey = 'id'
    // A counter example not matching this condition is a date filter which can be associated
    // with several keys: from, to, timerange
    prop.keys = [prop.name];
    prop.filterKey = prop.name; // Unique filter key, like id
    prop.consumeKeys = (keys) => {
      const index = keys.indexOf(prop.filterKey);
      if (index >= 0) {
        return keys.splice(index, 1);
      }
      return null;
    };
  } else {
    prop.consumeKeys = (keys) => {
      let ckeys = [];
      for (let i = 0; i < keys.length;) {
        if (prop.keys.indexOf(keys[i]) >= 0) {
          ckeys = ckeys.concat(keys.splice(i, 1));
        } else {
          i += 1;
        }
      }
      return ckeys.length && ckeys;
    };
  }
  if (!prop.sameFilters) {
    if (prop.filterKey) {
      prop.sameFilters = (f1, f2) => prop.sameValues(f1[prop.filterKey], f2[prop.filterKey]);
    } else {
      // Filter with several filter keys (date filter)
      prop.sameFilters = (f1, f2) => {
        return !prop.keys.find(k => !prop.sameValues(f1[k], f2[k]));
      };
    }
  }
  if (!prop.toQuery) {
    prop.toQuery = TYPES[prop.type || STRING_VALUE_TYPE].toQuery.bind(prop);
  }
  if (!prop.toFilter) {
    prop.toFilter = TYPES[prop.type || STRING_VALUE_TYPE].toFilter.bind(prop);
  }
  if (!prop.sameValues) {
    prop.sameValues = TYPES[prop.type || STRING_VALUE_TYPE].sameValues.bind(prop);
  }
  if (!prop.createObjectFilter) {
    prop.createObjectFilter = prop.type && TYPES[prop.type].createObjectFilter;
    if (!prop.createObjectFilter) {
      prop.createObjectFilter = (v, props, k) => o => prop.sameValues(o[k], v);
    }
  }
  prop.consumeKeys = (keys) => {
    let ckeys = [];
    for (let i = 0; i < keys.length;) {
      if (prop.keys.length === 1) {
        if (prop.keys[0] === keys[i]) {
          return keys.splice(i, 1);
        }
        i += 1;
      } else if (prop.keys.indexOf(keys[i]) >= 0) {
        ckeys = ckeys.concat(keys.splice(i, 1));
      } else {
        i += 1;
      }
    }
    return ckeys.length && ckeys;
  };
  return prop;
};

const getDefaultQueryValue = (pe, props) => {
  if (pe.getDefaultQueryValue) {
    return pe.getDefaultQueryValue(props);
  }
  return pe.defaultQueryValue;
};

const getDefaultValue = (pe, props) => {
  if (pe.getDefaultValue) {
    return pe.getDefaultValue(props);
  }
  return pe.defaultValue;
};

const withPageFilter = (Component, options) => {
  // Create a handler for handling URL query changes, create filter from query, etc...
  const createFilterHandler = (initProps) => {
    const filterEditor = typeof options === 'function' ? options(initProps) : options;
    const propertyEditors = (filterEditor.propertyEditors || []).map(procesEditor);
    const { searchKey } = filterEditor;

    const filterToQuery = filterEditor.filterToQuery || ((f, props, server) => {
      const keys = !f ? [] : Object.keys(f).slice();
      const queries = propertyEditors.map((pe) => {
        const editorKeys = pe.consumeKeys(keys);
        if (editorKeys) {
          const { filterKey } = pe;
          const value = filterKey && f[filterKey];
          const defaultValue = getDefaultValue(pe, props);
          // Queries for server and page URL do not behave the same way for default values
          // We want to hide them for the page URL and make them explicit for server queries
          if (server && filterKey
              && (defaultValue !== undefined)
              && ((value === undefined) || (value === defaultValue))) {
            const sv = value === undefined ? defaultValue : value;
            return `${filterKey}=${pe.toQuery(sv, server)}`;
          }
          if (filterKey) {
            // Manage values that don't generate a query
            const defaultQueryValue = getDefaultQueryValue(pe, props);
            if (!server && (defaultQueryValue !== undefined)) {
              if (defaultQueryValue === value) {
                return null;
              }
            } else if ((value === defaultValue) || (value === null)
                || (value === undefined) || (value.length === 0)) {
              return null;
            }
            const qv = pe.toQuery(value, server);
            if ((qv === undefined) || (qv === null)) {
              return null;
            }
            if (Array.isArray(qv)) {
              return qv.map(v => `${filterKey}=${v}`).join('&');
            }
            return `${filterKey}=${qv}`;
          }
          // Il multiple keys, toQuery is invoked with the filter and not a filter value
          return pe.toQuery(f, server);
        }
        return null;
      }).filter(x => x);
      return queries.length ? `?${queries.join('&')}` : '';
    });

    const queryToFilter = filterEditor.queryToFilter || ((q, props) => {
      const keys = !q ? [] : Object.keys(q).slice();
      const filter = {};
      let queryValid = true;
      propertyEditors.forEach((pe) => {
        const ckeys = pe.consumeKeys(keys);
        if (ckeys) {
          try {
            // Specific processing if property editor has only one filter key
            if (pe.filterKey) {
              filter[pe.filterKey] = pe.toFilter(q[pe.filterKey]);
              if (pe.validateValue) {
                const v = pe.validateValue(filter[pe.filterKey], props);
                if (v !== filter[pe.filterKey]) {
                  filter[pe.filterKey] = v;
                  queryValid = false;
                }
              }
            } else {
              // Multiple filter keys for this property editor
              // => it does not generate a filter value but a filter instead
              let pf = pe.toFilter(q);
              if (pe.validateFilter) {
                const vpf = pe.validateFilter(pf);
                if (!pe.sameFilters(pf, vpf)) {
                  pf = vpf;
                  queryValid = false;
                }
              }
              Object.assign(filter, pf);
            }
          } catch (e) {
            ckeys.forEach((k) => { delete filter[k]; });
            queryValid = false;
          }
        } else {
          const defaultQueryValue = getDefaultQueryValue(pe, props);
          const v = defaultQueryValue !== undefined
            ? defaultQueryValue : getDefaultValue(pe, props);
          if (v !== undefined) {
            // Add filter default values for keys not specified in the query
            if (pe.filterKey) {
              filter[pe.filterKey] = v;
            } else {
              Object.assign(filter, v);
            }
          }
        }
      });
      return { filter, queryValid };
    });

    const searchToQuery = (search, props) => {
      const q = filterEditor.searchToQuery ? filterEditor.searchToQuery(search, props) : search;
      return q ? `${searchKey}=${q}` : '';
    };

    const queryToSearch = (s, props) => {
      return filterEditor.queryToSearch ? filterEditor.queryToSearch(s, props) : s;
    };

    const createArrayFilter = filterEditor.createArrayFilter || ((f, props) => {
      const keys = !f ? [] : Object.keys(f).slice();
      if (!keys.length) {
        return null;
      }
      const filters = propertyEditors.map((pe) => {
        return pe.consumeKeys(keys)
            ? pe.createObjectFilter(pe.filterKey ? f[pe.filterKey] : f, props, pe.filterKey) : null;
      }).filter(x => x);
      if (!filters.length) {
        return null;
      }
      const oFilter = filters.length === 1 ? filters[0] : (o => !filters.find(of => !of(o)));
      // If flat array to be filtered, array can be filtered objects individually
      if (!options.dependsOn && !options.getParent && !options.getId) {
        return a => a.filter(oFilter);
      }
      // Array with dependencies, objects that are parent of accepted objects must also be accepeted
      const { dependsOn, getParent, getId } = options;
      return (a) => {
        if (!a) {
          return a;
        }
        // Build depedency graph
        const nodesById = {};
        const node = (id, o, parent, dep) => {
          if (!id) return null;
          let n = nodesById[id];
          if (!n) {
            n = { o, parent, dep };
            nodesById[id] = n;
          } else {
            if (parent) {
              n.parent = parent;
            }
            if (dep) {
              n.dep = dep;
            }
            if (o) {
              n.o = o;
            }
          }
          if (parent) {
            if (parent.children) {
              parent.children.push(n);
            } else {
              parent.children = [n];
            }
          }
          if (dep) {
            if (parent.depedencies) {
              parent.depedencies.push(n);
            } else {
              parent.depedencies = [n];
            }
          }
          return n;
        };

        const nodes = a.map(o => node(getId(o), o, getParent && node(getParent(o)), dependsOn && node(dependsOn(o))));
        // Filter the array
        const filterNode = (n) => {
          if (n.filtered === undefined) {
            let hasFilteredDeps = false;
            if (n.children) {
              hasFilteredDeps = !!n.children.find(filterNode);
            }
            if (!hasFilteredDeps && n.depedencies) {
              hasFilteredDeps = !!n.depedencies.find(filterNode);
            }
            n.filtered = hasFilteredDeps || oFilter(n.o);
          }
          return n.filtered;
        };
        const result = nodes.filter(filterNode).map(n => n.o);
        return result;
      };
    });

    // Provide a handler for converting filter property to filter and query
    return {
      server: filterEditor.server,
      filterEditor,
      sameFilters: filterEditor.sameFilter || ((f1 = {}, f2 = {}) => {
        const props1 = Object.keys(f1).filter(k => (f1[k] !== undefined) && (f1[k] !== null));
        const props2 = Object.keys(f2).filter(k => (f2[k] !== undefined) && (f2[k] !== null));
        if (!sameArrays(props1, props2)) {
          return false;
        }
        // diffEditor contains the first editor found for which its corresponding value(s) in f1
        // are different in f2
        const diffEditor = propertyEditors.find((pe) => {
          return pe.consumeKeys(props1) && !pe.sameFilters(f1, f2);
        });
        // f1 and f2 are equal if no editor was found with different values in f1 and f2
        return !diffEditor;
      }),
      filterToQuery,
      filterToServerQuery: filterEditor.filterToServerQuery || filterToQuery,
      queryToFilter,
      createArrayFilter,
      searchKey,
      sameSearch: filterEditor.sameSearch || ((s1, s2) => {
        if (!s1) return !s2;
        if (!s2) return false;
        return s1.toUpperCase() === s2.toUpperCase();
      }),
      searchToQuery,
      queryToSearch,
    };
  };

  class FilterComponent extends React.PureComponent {
    static getDerivedStateFromProps(props, state) {
      const { filterHandler } = state;
      const { searchKey } = filterHandler;
      let { query } = props;
      const s = searchKey && query && query[searchKey] || null;
      if (s) {
        query = { ...query };
        delete query[searchKey];
      }

      let newState = null;
      // Search
      if (searchKey) {
        const search = filterHandler.queryToSearch(s, props);
        if (props.route.context) {
          // Do not use setContext as it would trigger useless notifications.
          // Being here means the search in the query is the only source of truth for search in this page, not the context.
          // We duplicate the search info into the context for components
          // coupled with the route context, like the TablePanel, that initialize their filter
          // with the context search only.
          // We don't fear side effects as this FilterComponent is the top component of
          // the page components tree and component with route context will be rendered AFTER
          // the context is initialized to the true value.
          props.route.context.search = search || '';
        }
        if (!filterHandler.sameSearch(search, state.search)) {
          newState = { search };
        }
      }

      // Filter
      const { filter, queryValid } = filterHandler.queryToFilter(query, props);
      if (props.route.context) {
        // Do not use setContext as it would trigger useless notifications.
        // Being here means the filter in the query is the only source of truth for search in this page, not the context.
        // We duplicate the filter info into the context for components
        // coupled with the route context, like the TablePanel, that initialize their filter
        // with the context search only.
        // We don't fear side effects as this FilterComponent is the top component of
        // the page components tree and component with route context will be rendered AFTER
        // the context is initialized to the true value.
        // See http://github.compute.gurobi.com/Gurobi/grbrs-ng/issues/827
        if (queryValid) {
          props.route.context.filter = filter || '';
        }
      }

      if (!filterHandler.sameFilters(filter, state.filter) || (queryValid !== state.queryValid)) {
        newState = {
          ...(newState || {}),
          filter,
          arrayFilter: queryValid ? filterHandler.createArrayFilter(filter, props) : null,
          queryValid,
        };
        if (filterHandler.server) {
          newState.serverQuery = filterHandler.filterToServerQuery(filter, props, true);
        } else {
          newState.arrayFilter = queryValid ? filterHandler.createArrayFilter(filter, props) : null;
        }
      }
      return newState;
    }

    constructor(props) {
      super(props);

      this.handleFilterChange = this.handleFilterChange.bind(this);
      this.handleSearchChange = this.handleSearchChange.bind(this);
      this.state = {
        filterHandler: createFilterHandler(props),
      };
      this.ctxSubscriber = (ctx) => {
        const fh = this.state.filterHandler;
        if (!fh.sameFilters(ctx.filter, this.state.filter)
            || (fh.searchKey && !fh.sameSearch(ctx.search, this.state.search))) {
          this.updateRoute(ctx);
        }
      };
    }

    componentDidMount() {
      this.props.route.subscribeContextChange(this.ctxSubscriber);
    }

    componentWillUnmount() {
      this.props.route.unsubscribeContextChange(this.ctxSubscriber);
    }

    updateRoute = ({ filter, search }) => {
      const { history, location } = this.props;
      const { filterHandler } = this.state;
      let query = filterHandler.filterToQuery(filter, this.props);
      const searchQuery = filterHandler.searchKey && filterHandler.searchToQuery(search, this.props);
      if (searchQuery) {
        query = query ? `${query}&${searchQuery}` : `?${searchQuery}`;
      }
      history.push(`${location.pathname}${query ? encodeURI(query) : ''}`);
      if (this.props.route) {
        this.props.route.setLastVisitedLocation({
          pathname: location.pathname,
          query: query ? queryString.parse(query) : {},
        });
      }
    };

    handleFilterChange(filter) {
      const newContext = Object.assign({}, this.props.route.context, { filter });
      // Changing the context will trigger a the filter subscriber which in turns
      // will trigger a location query change.
      // This location change will be caught in getDerivedStateFromProps which
      // will generate a state update.
      this.props.route.setContext(newContext);
    }

    handleSearchChange(search) {
      const newContext = Object.assign({}, this.props.route.context, { search });
      // Changing the context will trigger a the filter subscriber which in turns
      // will trigger a location query change.
      // This location change will be caught in getDerivedStateFromProps which
      // will generate a state update.
      this.props.route.setContext(newContext);
    }

    render() {
      const { queryValid, filterHandler } = this.state;
      if (!queryValid) {
        return <Redirect to={`${this.props.location.pathname}${filterHandler.filterToQuery(this.state.filter, this.props)}`} />;
      }
      const props = {
        ...this.props,
        filter: this.state.filter,
        filterEditor: filterHandler.filterEditor,
        onFilterChange: this.handleFilterChange,
      };
      if (filterHandler.searchKey) {
        props.search = this.state.search;
        props.onSearchChange = this.handleSearchChange;
      }
      if (filterHandler.server) {
        props.serverQuery = this.state.serverQuery;
      } else {
        props.arrayFilter = this.state.arrayFilter;
      }
      return (
        <Component {...props} />
      );
    }
  }

  FilterComponent.propTypes = {
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    query: PropTypes.shape({}),
    route: PropTypes.shape({
      setContext: PropTypes.func,
      context: PropTypes.shape({
        filter: PropTypes.shape({}),
        filterQuery: PropTypes.string,
        search: PropTypes.string,
      }),
      subscribeContextChange: PropTypes.func,
      unsubscribeContextChange: PropTypes.func,
      setLastVisitedLocation: PropTypes.func,
    }).isRequired,
  };

  FilterComponent.defaultProps = {
    query: {},
  };

  return FilterComponent;
};

export default withPageFilter;
